import React, { useState } from 'react';
import Style from './formLayout.module.scss';

const FONT = 'inherit';   // Plus Jakarta Sans, heredada del body

export function ModalFormLayout({ children, footer, bodyStyle, footerStyle }) {
  return (
    <div style={s.layout}>
      <div className={Style.modalBody} style={{ ...s.body, ...bodyStyle }}>{children}</div>
      {footer && <div className={Style.footer} style={{ ...s.footer, ...footerStyle }}>{footer}</div>}
    </div>
  );
}

export function ModalFooter({ children }) {
  return <>{children}</>;
}

export function FlatButton({ variant = 'primary', onClick, disabled, children, type = 'button', style }) {
  const [hover, setHover] = useState(false);
  const [press, setPress] = useState(false);
  const base = variant === 'cancel' ? s.btnCancel : variant === 'neutral' ? s.btnNeutral : s.btnPrimary;
  const hoverStyle = hover && !press ? hoverByVariant[variant] || hoverByVariant.primary : {};
  const pressStyle = press ? pressByVariant[variant] || pressByVariant.primary : {};

  return (
    <button
      type={type}
      onClick={!disabled ? onClick : undefined}
      style={{ ...base, ...hoverStyle, ...pressStyle, ...(disabled ? s.disabled : {}), ...style }}
      onMouseEnter={() => !disabled && setHover(true)}
      onMouseLeave={() => { setHover(false); setPress(false); }}
      onMouseDown={() => !disabled && setPress(true)}
      onMouseUp={() => setPress(false)}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

export function FieldGrid({ children, columns = 2, style }) {
  return <div className={Style.fieldGrid} style={{ ...s.grid, gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, ...style }}>{children}</div>;
}

export function FormField({ label, error, fullWidth, children }) {
  return (
    <div style={fullWidth ? s.fullWidth : undefined}>
      {label && <label style={s.label}>{label}</label>}
      {children}
      {error && <span style={s.errorText}>{error}</span>}
    </div>
  );
}

export function InfoBox({ children, style }) {
  return <p style={{ ...s.infoBox, ...style }}>{children}</p>;
}

export function TextField({ label, error, fullWidth, readOnly, style, onKeyDown, onBlur, ...inputProps }) {
  const handleKeyDown = (e) => {
    if (inputProps.type === 'number' && (e.key === '-' || e.key === 'e' || e.key === '+')) {
      e.preventDefault();
    }
    if (onKeyDown) onKeyDown(e);
  };
  const handleBlur = (e) => {
    if (inputProps.type === 'number' && inputProps.min !== undefined && e.target.value !== '') {
      const val = parseFloat(e.target.value);
      const minVal = parseFloat(inputProps.min);
      if (!isNaN(val) && !isNaN(minVal) && val < minVal) {
        e.target.value = String(minVal);
        if (inputProps.onChange) inputProps.onChange(e);
      }
    }
    if (onBlur) onBlur(e);
  };
  return (
    <FormField label={label} error={error} fullWidth={fullWidth}>
      <input
        {...inputProps}
        readOnly={readOnly}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        style={{ ...s.input, ...(readOnly ? s.readOnly : {}), ...(error ? s.inputError : {}), ...style }}
      />
    </FormField>
  );
}

export function SelectField({ label, error, fullWidth, options = [], children, style, ...selectProps }) {
  return (
    <FormField label={label} error={error} fullWidth={fullWidth}>
      <select {...selectProps} style={{ ...s.select, ...(error ? s.inputError : {}), ...style }}>
        {children || options.map(option => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    </FormField>
  );
}

const buttonBase = {
  border: 'none',
  fontSize: 13,
  fontWeight: 650,
  cursor: 'pointer',
  padding: '8px 18px',
  borderRadius: 8,
  fontFamily: FONT,
  transition: 'background 0.15s, opacity 0.15s, transform 0.08s',
};

const s = {
  layout: { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, width: '100%' },
  body: { flex: 1, overflowY: 'auto', minHeight: 0, padding: '20px 24px 8px' },
  footer: {
    flexShrink: 0,
    borderTop: '1px solid #e5e7eb',
    padding: '14px 24px',
    display: 'flex',
    justifyContent: 'center',
    gap: 8,
    alignItems: 'center',
    background: 'white',
  },
  grid: { display: 'grid', gap: '10px 16px' },
  fullWidth: { gridColumn: '1 / -1' },
  label: {
    display: 'block',
    fontSize: 12,
    fontWeight: 650,
    color: '#374151',
    marginBottom: 4,
    letterSpacing: '0.01em',
    fontFamily: FONT,
  },
  input: {
    width: '100%',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: '7px 11px',
    fontSize: 13,
    color: '#111827',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: FONT,
    transition: 'border-color 0.15s, box-shadow 0.15s',
    background: 'white',
  },
  select: {
    width: '100%',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: '7px 11px',
    fontSize: 13,
    color: '#111827',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: FONT,
    background: 'white',
    cursor: 'pointer',
  },
  readOnly: { background: '#f8fafc', color: '#64748b' },
  inputError: { borderColor: '#ef4444', boxShadow: '0 0 0 3px rgba(239,68,68,0.1)' },
  errorText: { display: 'block', fontSize: 11, color: '#ef4444', marginTop: 3, fontFamily: FONT },
  infoBox: {
    margin: '0 0 16px',
    color: '#475569',
    fontSize: 12.5,
    background: '#f8f9fa',
    padding: '12px 16px',
    borderRadius: 8,
    borderLeft: '4px solid #FFB800',
    fontFamily: FONT,
  },
  // En reposo el boton va en tinte suave; al presionar toma el color pleno.
  btnCancel:  { ...buttonBase, background: '#FEE2E2', color: '#B91C1C', border: '1px solid #FECACA' },
  btnPrimary: { ...buttonBase, background: '#DCFCE7', color: '#15803D', border: '1px solid #BBF7D0' },
  btnNeutral: { ...buttonBase, background: '#F8FAFC', color: '#334155', border: '1px solid #E2E8F0' },
  disabled: { opacity: 0.55, cursor: 'not-allowed' },
};

const hoverByVariant = {
  cancel:  { background: '#FECACA', borderColor: '#FCA5A5' },
  primary: { background: '#BBF7D0', borderColor: '#86EFAC' },
  neutral: { background: '#F1F5F9', borderColor: '#CBD5E1' },
};

const pressByVariant = {
  cancel:  { background: '#DC2626', borderColor: '#DC2626', color: '#FFFFFF' },
  primary: { background: '#16A34A', borderColor: '#16A34A', color: '#FFFFFF' },
  neutral: { background: '#E2E8F0', borderColor: '#94A3B8', color: '#1E293B' },
};

export const modalFormStyles = s;
